import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditNumber } from 'components/forms/EditNumber';
import { EditRadioGroup } from 'components/forms/EditRadioGroup';
import { EditSelect } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { EditTime } from 'components/forms/EditTime';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';

import type { EditableRecurrence, IntervalRecurrenceUnit, RecurrenceMode } from './EditableRecurrence';
import { INTERVAL_UNIT_OPTIONS } from './intervalUnitOptions';

interface Props {
  readonly recurrence: EditableRecurrence;
}

/**
 * The fields of a single recurrence rule, as the recurrence-definition editor
 * shows them. A pure `observer` view over an `EditableRecurrence`: every control
 * reads a field and assigns it back, so there is no local state and no business
 * logic here — toggling the mode just sets `mode`, and the model decides what
 * that means at save time.
 *
 * The node-submit screen has its own view of the same model
 * (`SubmitScheduleFields`) rather than rendering options into this one.
 */
export const RecurrenceRuleFields = observer<Props>(({ recurrence }) => (
  <>
    <Row className="mb-3">
      <Col md={12}>
        <EditString label="Description" value={recurrence.description} onChange={(v) => (recurrence.description = v)} />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditRadioGroup<RecurrenceMode>
          value={recurrence.mode}
          onChange={(v) => (recurrence.mode = v)}
          options={[
            {
              // The fields below have no effect on a window_open rule. They live
              // in this option's content so the radio group disables rather than
              // hides them: the values survive a mode toggle.
              key: 'interval',
              value: 'interval',
              label: 'Interval',
              content: (
                <>
                  <Row className="mb-2">
                    <Col md={6}>
                      <EditSelect<IntervalRecurrenceUnit>
                        label="Unit"
                        value={recurrence.unit}
                        onChange={(v) => (recurrence.unit = v)}
                        mandatory
                        options={INTERVAL_UNIT_OPTIONS}
                      />
                    </Col>
                    <Col md={6}>
                      <EditNumber label="Interval" value={recurrence.interval} onChange={(v) => (recurrence.interval = v)} mandatory min={1} />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={6}>
                      <EditTime label="Start Time" value={recurrence.startTime} onChange={(v) => (recurrence.startTime = v)} />
                    </Col>
                    <Col md={6}>
                      <EditTimeZoneReference label="Time Zone" value={recurrence.startTimeZone} onChange={(v) => (recurrence.startTimeZone = v)} />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={12}>
                      <EditBoolean
                        label="Preserve wall-clock time"
                        value={recurrence.preserveTime}
                        onChange={(v) => (recurrence.preserveTime = v)}
                        info="When on, each occurrence keeps the same local clock time when the clocks change for daylight saving (e.g. always 09:00). When off, occurrences are spaced by an exact fixed interval, so the local time can shift by an hour across a DST change."
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <EditSchedulingWindowReference
                        label="During Scheduling Window"
                        value={recurrence.duringSchedulingWindow}
                        onChange={(v) => (recurrence.duringSchedulingWindow = v)}
                      />
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'window_open',
              value: 'window_open',
              label: 'On Scheduling Window Open',
              content: (
                <Row>
                  <Col md={12}>
                    <EditSchedulingWindowReference
                      label="Scheduling Window"
                      value={recurrence.schedulingWindow}
                      onChange={(v) => (recurrence.schedulingWindow = v)}
                      mandatory
                    />
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditBoolean
          label="Skip when window closed"
          value={recurrence.skipWhenWindowClosed}
          onChange={(v) => (recurrence.skipWhenWindowClosed = v)}
          info="Applies only when a node instance that uses this rule has a scheduling window set — not to During scheduling window on this rule (during always skips closed grid slots). When on, an occurrence outside the NI window is skipped and the next schedule slot is used. When off, execution is postponed to that NI window's next opening. Has no effect if the instance has no NI window."
        />
      </Col>
    </Row>
  </>
));
